import { useEffect, useMemo, useState } from "react";
import type { StoreApi } from "zustand";
import { revalidateLogic, useForm, useSelector } from "@tanstack/react-form";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  PasswordRequirementsList,
  useAuthStore,
  usePasswordRequirements,
  type AuthRepository,
  type AuthState,
} from "@nasam/core";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  PasswordField,
} from "@nasam/ui";

import { authRepository as defaultAuthRepository } from "../../auth";
import { AuthCardLayout, RequiredMark } from "./AuthCardLayout";

export interface ResetPasswordScreenProps {
  readonly authRepository?: AuthRepository;
  readonly authStore?: StoreApi<AuthState>;
}

/**
 * Step 3 of the OTP-based forgot-password flow: the resetToken now comes
 * from step 2 (verify-reset-otp), held in the shared authStore, not parsed
 * from an emailed link's URL -- the old link-status pre-check
 * (checkResetLink/LinkStatusView) is retired along with that link entirely.
 */
export function ResetPasswordScreen({
  authRepository = defaultAuthRepository,
  authStore = useAuthStore,
}: ResetPasswordScreenProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [resetToken, setResetToken] = useState(
    () => authStore.getState().passwordResetToken,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    return authStore.subscribe((state) => {
      setResetToken(state.passwordResetToken);
    });
  }, [authStore]);

  useEffect(() => {
    if (!success && authStore.getState().passwordResetToken === null) {
      void navigate({ to: "/auth/forgot-password", replace: true });
    }
  }, [authStore, navigate, success]);

  const schema = useMemo(
    () =>
      z
        .object({
          newPassword: z
            .string()
            .min(1, t("validation:passwordRequired"))
            .min(8, t("validation:passwordMinLength"))
            .regex(/[A-Z]/, t("validation:passwordUppercase"))
            .regex(/[a-z]/, t("validation:passwordLowercase"))
            .regex(/[0-9]/, t("validation:passwordNumber"))
            .regex(/[^A-Za-z0-9]/, t("validation:passwordSpecialChar")),
          confirmPassword: z
            .string()
            .min(1, t("validation:confirmPasswordRequired")),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          message: t("validation:passwordsDoNotMatch"),
          path: ["confirmPassword"],
        }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const onSubmit = async (values: FormValues) => {
    setErrorMessage(null);
    if (resetToken === null) {
      void navigate({ to: "/auth/forgot-password", replace: true });
      return;
    }
    try {
      await authRepository.resetPassword({
        resetToken,
        newPassword: values.newPassword,
      });
      // Cleared on "Proceed to Login" below, not here -- clearing it
      // immediately would drop passwordResetToken to null while this route
      // is still active and re-trigger the "no token, bounce to
      // forgot-password" guard before the success view ever renders.
      setSuccess(true);
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : t("common:somethingWentWrong"),
      );
    }
  };

  const form = useForm({
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    } satisfies FormValues,
    validationLogic: revalidateLogic({ mode: "change" }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => onSubmit(value),
  });
  const newPassword = useSelector(
    form.store,
    (state) => state.values.newPassword,
  );
  const passwordRequirements = usePasswordRequirements(newPassword);

  useEffect(() => {
    if (!form.state.isValid) void form.validate("change");
  }, [i18n.language, form]);

  if (success) {
    return (
      <AuthCardLayout
        title={t("authentication:resetPasswordSuccessTitle")}
        subtitle={t("authentication:resetPasswordSuccessDescription")}
        illustrationSrc="/brand/password-illustration.png"
        footer={
          <Button
            type="button"
            variant="primary"
            className="h-13 w-full"
            onClick={() => {
              authStore.getState().clearPasswordReset();
              void navigate({ to: "/auth/login" });
            }}
          >
            {t("authentication:proceedToLogin")}
          </Button>
        }
      />
    );
  }

  return (
    <AuthCardLayout
      title={t("authentication:resetPasswordTitle")}
      subtitle={t("authentication:resetPasswordSubtitle")}
      illustrationSrc="/brand/password-illustration.png"
      footer={
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              type="submit"
              form="reset-password-form"
              variant="primary"
              className="h-13 w-full"
              disabled={isSubmitting}
            >
              <span
                key={isSubmitting ? "busy" : "idle"}
                className="animate-in fade-in-0 duration-150"
              >
                {isSubmitting
                  ? t("authentication:resettingPasswordButton")
                  : t("authentication:resetPasswordButton")}
              </span>
            </Button>
          )}
        </form.Subscribe>
      }
    >
      <Form
        form={form}
        id="reset-password-form"
        className="flex w-full flex-col gap-4 px-4 md:px-13 py-2"
        noValidate
      >
        {errorMessage !== null && (
          <div
            role="alert"
            className="animate-in fade-in-0 slide-in-from-top-1 rounded-md border border-fail-text bg-container-default-background p-3 text-body-s-r text-fail-text"
          >
            {errorMessage}
          </div>
        )}

        <form.Field name="newPassword">
          {(field) => (
            <FormField field={field}>
              <FormItem className="flex flex-col gap-2 space-y-0 animate-in fade-in-0 slide-in-from-bottom-1">
                <FormLabel
                  htmlFor="reset-new-password"
                  className="flex items-center gap-1 text-body-m-r"
                >
                  {t("authentication:newPasswordLabel")}
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <PasswordField
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    id="reset-new-password"
                    autoComplete="new-password"
                    placeholder={t("authentication:newPasswordPlaceholder")}
                    hidePasswordLabel={t("authentication:hidePassword")}
                    showPasswordLabel={t("authentication:showPassword")}
                    className="h-11 bg-container-hover px-4 py-2 text-start text-body-s-r placeholder:text-body-s-r"
                  />
                </FormControl>
                <FormMessage className="text-start" />
              </FormItem>
            </FormField>
          )}
        </form.Field>

        <form.Field name="confirmPassword">
          {(field) => (
            <FormField field={field}>
              <FormItem className="flex flex-col gap-2 space-y-0 animate-in fade-in-0 slide-in-from-bottom-1 [animation-delay:60ms]">
                <FormLabel
                  htmlFor="reset-confirm-password"
                  className="flex items-center gap-1 text-body-m-r"
                >
                  {t("authentication:confirmPasswordLabel")}
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <PasswordField
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    id="reset-confirm-password"
                    autoComplete="new-password"
                    placeholder={t("authentication:confirmPasswordPlaceholder")}
                    hidePasswordLabel={t("authentication:hidePassword")}
                    showPasswordLabel={t("authentication:showPassword")}
                    className="h-11 bg-container-hover px-4 py-2 text-start text-body-s-r placeholder:text-body-s-r"
                  />
                </FormControl>
                <FormMessage className="text-start" />
              </FormItem>
            </FormField>
          )}
        </form.Field>

        <PasswordRequirementsList requirements={passwordRequirements} />
      </Form>
    </AuthCardLayout>
  );
}
